/** Supervisor metrics: papers served, and each deputy's papers, attempts, miles and idle time. */
import { useEffect, useState } from 'react';
import { api } from '../shared/api.ts';
import { useAuth } from '../shared/auth.tsx';
import { Card, Empty, ErrorBox, Field, Spinner, useLoad } from '../shared/ui.tsx';

const iso = (tz: string, d: Date) => new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
const addDays = (s: string, n: number) => new Date(Date.parse(`${s}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
const hm = (min: number) => (min >= 60 ? `${Math.floor(min / 60)}h ${String(min % 60).padStart(2, '0')}m` : `${min}m`);
const STATUS: Record<string, string> = { off: 'Off duty', available: 'On duty', on_scene: 'Out of car', unavailable: 'Unavailable' };

export function Metrics() {
  const { me } = useAuth();
  const tz = me!.agency.timezone;
  const today = iso(tz, new Date());
  const weekStart = addDays(today, -new Date(`${today}T12:00:00Z`).getUTCDay());
  const presets: [string, string, string][] = [
    ['Today', today, today],
    ['Yesterday', addDays(today, -1), addDays(today, -1)],
    ['This week', weekStart, today],
    ['This month', today.slice(0, 8) + '01', today],
  ];
  const [range, setRange] = useState({ from: today, to: today });
  const [tick, setTick] = useState(0);
  const r = useLoad(() => api.get<any>(`/api/metrics?from=${range.from}&to=${range.to}`), [range.from, range.to, tick]);
  const live = range.to === today;
  useEffect(() => {
    if (!live) return;
    const t = setInterval(() => setTick((x) => x + 1), 60_000);
    return () => clearInterval(t);
  }, [live]);
  const label = presets.find((p) => p[1] === range.from && p[2] === range.to)?.[0] ?? `${range.from} – ${range.to}`;
  const t = r.data?.totals;
  return (
    <div className="stack">
      <div>
        <h1>Metrics</h1>
        <p className="muted">Papers served and what each deputy is doing. Miles and idle time come from the deputy’s location while on duty.</p>
      </div>
      <div className="row gap wrap mx-range">
        {presets.map(([l, f, to]) => (
          <button key={l} className={`btn sm ${range.from === f && range.to === to ? 'primary' : ''}`} onClick={() => setRange({ from: f, to })}>
            {l}
          </button>
        ))}
        <Field label="From">
          <input type="date" value={range.from} max={range.to} onChange={(e) => e.target.value && setRange({ ...range, from: e.target.value })} />
        </Field>
        <Field label="To">
          <input type="date" value={range.to} min={range.from} onChange={(e) => e.target.value && setRange({ ...range, to: e.target.value })} />
        </Field>
        {live && <span className="small muted">Updates every minute</span>}
      </div>
      <ErrorBox error={r.error} />
      {!r.data ? (
        <Spinner />
      ) : (
        <>
          <div className="tiles mx-tiles">
            <div className="tile">
              <div className="tile-n">{t.served}</div>
              <div className="tile-l">Papers served · {label}</div>
              <div className="small muted">
                {t.personal} personal · {t.substitute} substitute{t.posted ? ` · ${t.posted} posted` : ''}
              </div>
            </div>
            <div className="tile">
              <div className="tile-n">{t.serveRate == null ? '—' : `${t.serveRate}%`}</div>
              <div className="tile-l">Served vs. non-served</div>
              <div className="small muted">{t.non_served} non-served</div>
            </div>
            <div className="tile">
              <div className="tile-n">{t.attempts}</div>
              <div className="tile-l">Attempts</div>
              <div className="small muted">{r.data.teamMiles} mi driven</div>
            </div>
            <div className="tile">
              <div className="tile-n">{t.received}</div>
              <div className="tile-l">Papers received</div>
            </div>
            <div className="tile">
              <div className="tile-n">{t.in_field_now}</div>
              <div className="tile-l">In the field now</div>
              <div className="small muted">{t.ready_now} ready to assign</div>
            </div>
          </div>
          <Card title={`Deputies · ${label}`}>
            {r.data.deputies.length === 0 ? (
              <Empty>No deputies.</Empty>
            ) : (
              <div className="table-wrap">
                <table className="table mx-table">
                  <thead>
                    <tr>
                      <th>Deputy</th>
                      <th>Now</th>
                      <th className="num">Papers held</th>
                      <th className="num">Attempted</th>
                      <th className="num">Served</th>
                      <th className="num">Miles</th>
                      <th className="num">On duty</th>
                      <th className="num">Idle</th>
                      <th>Last activity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.data.deputies.map((d: any) => (
                      <tr key={d.userId}>
                        <td>
                          <strong>{d.name}</strong>
                          {d.badge && <span className="muted small"> · {d.badge}</span>}
                        </td>
                        <td>
                          <span className={`mx-dot s-${d.status}`} /> {STATUS[d.status] ?? d.status}
                        </td>
                        <td className="num">
                          {d.held}
                          {d.assigned > d.held && <div className="small muted">{d.assigned - d.held} to pick up</div>}
                        </td>
                        <td className="num">
                          {d.papersAttempted}
                          {d.attempts > d.papersAttempted && <div className="small muted">{d.attempts} attempts</div>}
                        </td>
                        <td className="num">{d.served}</td>
                        <td className="num">{d.miles}</td>
                        <td className="num">{d.dutyMinutes ? hm(d.dutyMinutes) : '—'}</td>
                        <td className={`num ${d.idleMinutes >= 60 ? 'mx-hot' : ''}`}>{d.dutyMinutes || d.idleMinutes ? hm(d.idleMinutes) : '—'}</td>
                        <td className="small">
                          {d.lastActivityAt ? (
                            <>
                              {new Date(d.lastActivityAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz })}
                              {d.lastActivity ? <span className="muted"> · {d.lastActivity}</span> : null}
                            </>
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="small muted">
              Idle = parked in one spot 10+ minutes while on duty, not counting time out of the car at a paper. Miles are straight-line between location fixes, so winding roads read a little
              short.
            </p>
          </Card>
        </>
      )}
    </div>
  );
}
